import { useCallback, useState } from "react";
import useTabStore from "@/stores/useTabStore";

/**
 * A widget's saved highlight order (`WidgetFilterData.highlightOrder`) —
 * mirroring `useWidgetFilterState` / `useWidgetTableState`.
 *
 * With `persistKey` it reads and writes the active tab's entry for that widget
 * instance, so it is saved with the layout and shared by a popped-out copy of
 * the same widget. Without one (a widget rendered outside a tab) it is local
 * state that isn't saved, as that widget's selections already are.
 *
 * `undefined` means the widget has never had an order saved; see
 * effectiveOrder for what applies then.
 */
export function useWidgetHighlightOrder(
  persistKey: string | undefined
): [string[] | undefined, (order: string[]) => void] {
  const [localOrder, setLocalOrder] = useState<string[] | undefined>(undefined);

  const storeOrder = useTabStore(
    useCallback(
      (state) => {
        if (!persistKey) return undefined;
        const tab = state.tabs.find((t) => t.id === state.activeTabId);
        return tab?.widgetFilters?.[persistKey]?.highlightOrder;
      },
      [persistKey]
    )
  );

  const setStoreOrder = useCallback(
    (order: string[]) => {
      if (!persistKey) return;
      useTabStore.getState().setWidgetHighlightOrder(persistKey, order);
    },
    [persistKey]
  );

  if (!persistKey) return [localOrder, setLocalOrder];
  return [storeOrder, setStoreOrder];
}
